import React, {
  FunctionComponent,
  useReducer,
  useContext,
  useEffect,
} from 'react';
import { useMutation } from 'react-apollo';
import { Auth, Hub } from 'aws-amplify';
import { includes, pathOr } from 'ramda';
import ReactGA from 'react-ga4';

import { zendeskInit } from 'config';

import { SET_PROFILE, CREATE_SPLITIO_CLIENT } from './UserMgmt.queries';
import { CurrentUserAttributes, checkUserType } from './UserMgmt.helpers';

type Props = {
  children: React.ReactNode;
};

type State = [AuthState, Function];

export type UserType =
  | 'enterprise'
  | 'agency'
  | 'tier3'
  | 'dosh'
  | 'demo'
  | null;

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  userType: UserType;
  merchantName: string;
  merchantId: string;
};

type Action =
  | { type: 'logout' }
  | { type: 'never' }
  | { type: 'login'; currentUser: CurrentUserAttributes };

type PayLoad = {
  payload: {
    event: string;
    data: any;
  };
};

const initAuthState = {
  isAuthenticated: false,
  isLoading: true,
  userType: null,
  merchantName: '',
  merchantId: '',
};

const externalUserTypes = ['enterprise', 'demo', 'tier3', 'agency'];

const checkStorage = (dispatch: Function) => () => {
  if (localStorage.length === 0) {
    dispatch({
      type: 'logout',
    });
  }

  const prefixKey = `CognitoIdentityServiceProvider.${process.env.COGNITO_USER_POOL_WEB_CLIENT_ID}`;

  const userKey = localStorage.getItem(`${prefixKey}.LastAuthUser`);
  const idToken = localStorage.getItem(`${prefixKey}.${userKey}.idToken`);
  const accessToken = localStorage.getItem(
    `${prefixKey}.${userKey}.accessToken`,
  );
  const refreshToken = localStorage.getItem(
    `${prefixKey}.${userKey}.refreshToken`,
  );
  if (!userKey || !idToken || !accessToken || !refreshToken) {
    dispatch({
      type: 'logout',
    });
  }
  return {
    idToken,
    accessToken,
    refreshToken,
  };
};

const checkSession = async (
  dispatch: Function,
  setProfile: Function,
  createSplitioClient: Function,
) => {
  try {
    await Auth.currentSession();
    const currentUser = await Auth.currentAuthenticatedUser();

    setProfile({ variables: { profile: currentUser.attributes } });
    createSplitioClient({ variables: { key: currentUser.attributes.email } });

    dispatch({
      type: 'login',
      currentUser: currentUser.attributes,
    });
  } catch (error) {
    dispatch({
      type: 'logout',
    });
  }
};

const getCurrentUser = async (
  dispatch: Function,
  setProfile: Function,
  createSplitioClient: Function,
) => {
  // amplify auth event listener
  Hub.listen('auth', async ({ payload: { event, data } }: PayLoad) => {
    switch (event) {
      case 'signIn':
        const currentUser = await Auth.currentAuthenticatedUser();
        await setProfile({ variables: { profile: currentUser.attributes } });

        ReactGA.set({ page: 'login' });
        ReactGA.set({ userId: currentUser.attributes.sub });

        createSplitioClient({
          variables: { key: currentUser.attributes.email },
        });
        dispatch({
          type: 'login',
          currentUser: currentUser.attributes,
        });
        break;
      case 'signUp':
        console.info('user signed up');
        break;
      case 'signOut':
        dispatch({
          type: 'logout',
        });
        break;
      case 'signIn_failure':
        dispatch({
          type: 'logout',
        });
        break;
    }
  });
};

export const signOut = async (): Promise<void> => {
  try {
    sessionStorage.removeItem('referrer');
    await Auth.signOut({ global: true });
  } catch (error) {
    console.error('signOut error: ', error);
  }
};

const UserMgmtContext = React.createContext<State>([initAuthState, () => {}]);

const authReducer = (state: AuthState, action: Action) => {
  switch (action.type) {
    case 'logout': {
      return {
        isLoading: false,
        isAuthenticated: false,
        userType: null,
        merchantName: '',
        merchantId: '',
      };
    }
    case 'login': {
      return {
        isLoading: false,
        isAuthenticated: true,
        userType: checkUserType(action.currentUser),
        merchantName: pathOr(
          null,
          ['currentUser', 'custom:merchantName'],
          action,
        ),
        merchantId: pathOr(null, ['currentUser', 'custom:merchantId'], action),
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

export const UserMgmtProvider: FunctionComponent<Props> = (props) => {
  const [authState, dispatch] = useReducer<any>(authReducer, {
    isAuthenticated: false,
    isLoading: true,
    userType: null,
  });

  const [setProfile] = useMutation(SET_PROFILE);
  const [createSplitioClient] = useMutation(CREATE_SPLITIO_CLIENT);
  const value = React.useMemo(() => [authState, dispatch], [authState]);
  const { isAuthenticated, isLoading }: any = authState;

  useEffect(() => {
    getCurrentUser(dispatch, setProfile, createSplitioClient);
    checkSession(dispatch, setProfile, createSplitioClient);
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      zendeskInit((authState as AuthState).userType);
    }
  }, [isAuthenticated]);

  return <UserMgmtContext.Provider value={value as State} {...props} />;
};

export const useUserMgmt = () => {
  const [authState, dispatch] = useContext(UserMgmtContext);
  const { isAuthenticated, isLoading, userType } = authState;
  const isExternalUser = includes(userType as string, externalUserTypes);
  const isTier3 = userType === 'tier3';
  const isDemoUser = userType === 'demo';

  return {
    ...authState,
    dispatch,
    isAuthenticated,
    isLoading,
    userType,
    isExternalUser,
    isTier3,
    isDemoUser,
    isInternalUser: userType === 'dosh',
    checkStorage: checkStorage(dispatch),
  };
};
